import React, { Fragment, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import Mask from '../../masks/input-mask';
import Tooltip from '../../tooltip/TooltipWrapper';
import '../../tooltip/tooltip.css';

import './Input.css';

import SuccessSVG from '../../icons/SuccessSVG';
import InvalidSVG from '../../icons/InvalidSVG';

function Input(props) {
  let errorClasses =
    'placeholder-middle-grey font-museo font-w-300 p-15 leading-22 text-17 h-55 border-error-red border-solid border-1 inputShadow ' +
    (props.textboxClasses ? props.textboxClasses : '');
  let textboxClasses =
    'placeholder-middle-grey font-museo font-w-300 p-15 leading-22 text-17 h-55 border-middle-grey border-solid border-1 inputShadow ' +
    (props.textboxClasses ? props.textboxClasses : '');
  let spacingClasses = 'pl-5 pr-5 md:pl-0 md:pr-0 m-0 flex flex-col relative ' + (props.spacingClasses ? props.spacingClasses : '');
  const { register, isFormValidating } = useFormContext();

  const [isTouched, setIsTouched] = useState(false);
  const [isValid, setIsValid] = useState(false);

  const onBlurHandler = () => {
    setIsTouched(true);
    // Leave this for forms that don't use custom yupValidationResolver (no long async tasks)
    if (!props.error && isFormValidating === undefined) {
      setIsValid(true);
    }
  };

  const onChangeHandler = (e, prefix) => {
    const value = e.target.value;
    // This is trivial implementation
    // Should probably use regex, but this is difficult to implement without knowing what the prefix is (some, like $, require escaping, while others don't)
    e.target.value = `${prefix}${value.replace(prefix, '')}`;
  };

  useEffect(() => {
    if (isTouched && !isFormValidating) {
      setIsValid(!props.error);
    }
  }, [isFormValidating, props.error]);

  return (
    <div className={spacingClasses} id={props.tooltipName}>
      <label
        className={'mb-5 font-mont-700 font-w-400 text-12 tracking-1 uppercase flex relative ' + (props.tooltip ? 'leading-17 -mt-5' : 'leading-12')}
        htmlFor={props.for}
      >
        {props.label}
        {props.optional && <span className="text-14 leading-14 font-museo font-w-300 text-almost-black italic capitalize">&nbsp;(Optional)</span>}
        {props.tooltip && (
          <Tooltip
            aria={{ content: 'describedby', expanded: false }}
            content={props.tooltip}
            trigger="click"
            role="tooltip"
            interactive={true}
            animation={'fade'}
            duration={250}
            popperOptions={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 20],
                  },
                },
                {
                  name: 'flip',
                  options: {
                    fallbackPlacements: ['top', 'bottom'],
                    allowedAutoPlacements: ['top', 'bottom'],
                    padding: 50,
                  },
                },
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: typeof window !== 'undefined' ? document.querySelector(`#${props.tooltipName}`) : 'viewport',
                    padding: 0,
                  },
                },
              ],
            }}
          >
            <button type="button" aria-label={"Additional information about the '" + props.label + "' field"} className="rounded-full leading-17 ml-5">
              <svg
                aria-hidden="true"
                data-icon="info-circle"
                className="text-info-blue"
                style={{ width: '17px' }}
                role="img"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
              >
                <path
                  fill="currentColor"
                  d="M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 110c23.196 0 42 18.804 42 42s-18.804 42-42 42-42-18.804-42-42 18.804-42 42-42zm56 254c0 6.627-5.373 12-12 12h-88c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h12v-64h-12c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h64c6.627 0 12 5.373 12 12v100h12c6.627 0 12 5.373 12 12v24z"
                ></path>
              </svg>
            </button>
          </Tooltip>
        )}
      </label>
      {props.mask && (
        <Mask
          type={props.type || 'text'}
          ref={register}
          name={props.for}
          id={props.for}
          maxLength={props.length}
          className={props.error ? errorClasses : textboxClasses}
          placeholder={props.placeholder}
          onBlur={onBlurHandler}
          mask={props.mask}
          aria-invalid={props.error != undefined}
          aria-describedby={'error-' + props.for + ' caption-' + props.for}
          inputMode={props.inputMode}
          onChange={props.prefix != null ? e => onChangeHandler(e, props.prefix) : undefined}
        />
      )}

      {!props.mask && (
        <input
          type={props.type || 'text'}
          ref={register}
          name={props.for}
          id={props.for}
          maxLength={props.length}
          className={props.error ? errorClasses : textboxClasses}
          placeholder={props.placeholder}
          onBlur={onBlurHandler}
          aria-invalid={props.error != undefined}
          aria-describedby={'error-' + props.for + ' caption-' + props.for}
          inputMode={props.inputMode}
          onChange={props.prefix != null ? e => onChangeHandler(e, props.prefix) : undefined}
        />
      )}

      {isTouched && isValid && !props.error && !props.optional && (
        <SuccessSVG fill="#008040" height="16" width="16" viewbox="0 0 70 70" classes="absolute top-37 right-15" />
      )}
      {props.error && <InvalidSVG fill="#d42b47" height="16" width="16" viewbox="0 0 70 70" classes="absolute top-37 right-15" />}

      <small id={'error-' + props.for} className="text-error-red absolute" style={{ bottom: '10px' }} aria-live="assertive">
        {props.error && !props.error.type.startsWith('custom-') && (
          <Fragment>
            {props.label.replace('?', '')} is {props.error.type === 'required' ? 'required' : 'invalid'}
          </Fragment>
        )}
        {props.error && props.error.type.startsWith('custom-') && <Fragment>{props.error.message}</Fragment>}
      </small>

      <small id={'caption-' + props.for} className="text-14 leading-18 font-w-400 text-dark-grey absolute" style={{ bottom: '8px' }} aria-live="polite">
        {props.caption && !props.error && <Fragment>{props.caption}</Fragment>}
      </small>
    </div>
  );
}

export default Input;
